import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import BusinessMpProvider from "@/core/data-provider/data-mp/business/business-mp-provider";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import {
  selectedImprovementSelector,
  setImprovementsList,
  setSelectedImprovement,
} from "@/domains/business/slice";
import toast from "@/domains/toasts/helpers/toast";

export default function useImprovements(businessId: number) {
  const { t, i18n } = useTranslation("business");
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [isBuying, setIsBuying] = useState(false);

  const selectedImprovement = useAppSelector(selectedImprovementSelector);

  const prevSelectedImprovementRef = useRef(selectedImprovement);

  const load = useCallback(async () => {
    if (businessId) {
      setIsLoading(true);

      try {
        const response = await BusinessMpProvider.getImprovements(businessId);

        if (response instanceof RpcMpProviderSuccessResponse) {
          const improvementsData = response.data;
          dispatch(setImprovementsList(improvementsData));

          if (
            selectedImprovement &&
            improvementsData &&
            selectedImprovement === prevSelectedImprovementRef.current
          ) {
            const updatedImprovement = improvementsData.find(
              (item) => item.id === selectedImprovement.id,
            );
            if (
              updatedImprovement &&
              JSON.stringify(updatedImprovement) !== JSON.stringify(selectedImprovement)
            ) {
              dispatch(setSelectedImprovement(updatedImprovement));
            }
          }
          prevSelectedImprovementRef.current = selectedImprovement;
        } else if (i18n.exists(`business:business.errors.${response.error.message}`)) {
          console.error(response.error.message);
          toast.error(t(`business.errors.${response.error.message}`));
        } else {
          console.error(response.error.message);
          toast.error(t("business.toast.improvementsLoadError"));
        }
      } catch (e) {
        console.error(e);
        toast.error(t("business.toast.improvementsLoadError"));
      } finally {
        setIsLoading(false);
      }
    }
  }, [businessId, i18n, dispatch, selectedImprovement, t]);

  useEffect(() => {
    load();
  }, [load]);

  const buyImprovement = useCallback(
    async (improvementId: number) => {
      if (businessId && improvementId) {
        setIsBuying(true);

        try {
          const response = await BusinessMpProvider.improveBusiness(businessId, improvementId);

          if (response instanceof RpcMpProviderSuccessResponse) {
            toast.success(t("business.toast.improvementsBuySuccess"));
            load();
          } else if (i18n.exists(`business:business.errors.${response.error.message}`)) {
            console.error(response.error.message);
            toast.error(t(`business.errors.${response.error.message}`));
          } else {
            console.error(response.error.message);
            toast.error(t("business.toast.improvementsBuyError"));
          }
        } catch (e) {
          console.error(e);
          toast.error(t("business.toast.improvementsBuyError"));
        } finally {
          setIsBuying(false);
        }
      }
    },
    [businessId, i18n, load, t],
  );

  return {
    isLoading,
    buyImprovement,
    isBuying,
  };
}
